/**
 * @file MyEnrollment.component.jsx
 * @description Displays current user's USABG University enrollment status
 *
 * This component shows:
 * - Session details (date, location)
 * - Status timeline (Enrolled/Invited → Accepted → Paid → Checked In → Completed)
 * - Action buttons based on current status:
 *   - Accept Invitation (if invited but not accepted)
 *   - Pay Deposit (if enrolled/accepted but not paid)
 * - Payment verification after Stripe redirect
 *
 * Payment Flow:
 * 1. User clicks "Pay Deposit"
 * 2. Redirected to Stripe Checkout
 * 3. On success, redirected back with ?payment=success&enrollment_id=X
 * 4. Component verifies payment or waits for webhook
 * 5. Updates UI to show paid status
 *
 * @see /docs/modules/UsabgUniversity/README.md for full documentation
 */
import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { MDBCard, MDBCardBody, MDBCardHeader, MDBBtn, MDBBadge, MDBSpinner, MDBAlert } from 'mdb-react-ui-kit'
import { format, parseISO } from 'date-fns'
import UniversityStore from '../../store/University.store'

import './MyEnrollment.component.scss'

/**
 * Configuration for payment verification behavior
 * @constant {Object}
 * @property {boolean} ENABLE_REDIRECT_VERIFICATION - If true, verifies payment via API call after redirect.
 *   If false, relies on Stripe webhook to update record (polls for update).
 */
const CONFIG = {
  ENABLE_REDIRECT_VERIFICATION: true,
}

/**
 * MyEnrollment Component
 *
 * Displays the current user's enrollment status in USABG University.
 * Only renders if the user has an active enrollment in an upcoming session.
 *
 * @component
 * @example
 * <MyEnrollment />
 */
const MyEnrollment = observer(() => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false)
  const [paymentError, setPaymentError] = useState(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)

  // Fetch enrollment on mount and check for payment success redirect
  useEffect(() => {
    const init = async () => {
      await UniversityStore.fetchMyEnrollment()

      // Check if redirected from successful payment
      const urlParams = new URLSearchParams(window.location.search)
      const paymentStatus = urlParams.get('payment')
      const enrollmentId = urlParams.get('enrollment_id')

      if (paymentStatus === 'success' && enrollmentId) {
        const sessionId = urlParams.get('session_id')
        // Clear URL params
        window.history.replaceState({}, '', window.location.pathname)

        if (CONFIG.ENABLE_REDIRECT_VERIFICATION) {
          setIsVerifyingPayment(true)

          const result = await UniversityStore.verifyPayment(enrollmentId, sessionId)

          if (result.verified) {
            setPaymentSuccess(true)
          } else if (result.message) {
            setPaymentError(result.message)
          }

          setIsVerifyingPayment(false)
        } else {
          // Webhook mode: just show success message, webhook will update the record
          setPaymentSuccess(true)
          // Poll for webhook update - retry every 2 seconds for up to 30 seconds
          const pollForUpdate = async (attempts = 0) => {
            if (attempts >= 15) return // Stop after 30 seconds
            await UniversityStore.fetchMyEnrollment()
            // Check if paid_at is now set
            if (!UniversityStore.myEnrollment?.paid_at) {
              setTimeout(() => pollForUpdate(attempts + 1), 2000)
            }
          }
          setTimeout(() => pollForUpdate(), 2000)
        }
      } else if (paymentStatus === 'cancelled') {
        setPaymentError('Payment was cancelled.')
        window.history.replaceState({}, '', window.location.pathname)
      }
    }

    init()
  }, [])

  // Don't render if loading or no enrollment
  if (!UniversityStore.isEnrolled) {
    return null
  }

  const enrollment = UniversityStore.myEnrollment
  const session = enrollment?.session

  const formatDateRange = (startDate, endDate) => {
    try {
      // Extract just YYYY-MM-DD portion and parse as local date to prevent timezone rollover
      const toLocalDate = dateStr => {
        const dateOnly = String(dateStr).split('T')[0].split(' ')[0]
        const [year, month, day] = dateOnly.split('-').map(Number)
        return new Date(year, month - 1, day)
      }
      const start = toLocalDate(startDate)
      const end = toLocalDate(endDate)
      return `${format(start, 'MMMM d')} - ${format(end, 'd, yyyy')}`
    } catch {
      return `${startDate} - ${endDate}`
    }
  }

  const getStatusBadge = () => {
    // Override status to 'paid' when payment just completed
    const status = paymentSuccess && !enrollment.paid_at ? 'paid' : UniversityStore.enrollmentStatus

    const statusConfig = {
      completed: { color: 'success', label: 'Completed', icon: 'check-circle' },
      in_progress: { color: 'info', label: 'In Progress', icon: 'spinner' },
      paid: { color: 'success', label: 'Paid - Ready', icon: 'check' },
      accepted: { color: 'primary', label: 'Accepted', icon: 'thumbs-up' },
      invited: {
        color: 'warning',
        label: 'Invitation Pending',
        icon: 'envelope',
      },
      enrolled: { color: 'info', label: 'Enrolled', icon: 'user-check' },
      pending: { color: 'secondary', label: 'Pending', icon: 'clock' },
    }

    const config = statusConfig[status] || statusConfig.pending

    return (
      <MDBBadge color={config.color} className='status-badge'>
        <i className={`fas fa-${config.icon} mr-1`}></i>
        {config.label}
      </MDBBadge>
    )
  }

  const handlePayDeposit = async () => {
    setIsProcessingPayment(true)
    setPaymentError(null)

    const result = await UniversityStore.createPaymentCheckout(enrollment.id)

    if (!result.success) {
      setPaymentError(result.error)
      setIsProcessingPayment(false)
    }
    // If success, user is redirected to Stripe
  }

  const handleAcceptInvitation = async () => {
    const result = await UniversityStore.acceptInvitation(enrollment.id)
    if (!result.success) {
      setPaymentError(result.error)
    }
  }

  const needsToAccept = enrollment.invited_at && !enrollment.accepted_at
  const needsToPay = !enrollment.paid_at && (enrollment.requested_at || enrollment.accepted_at)

  return (
    <MDBCard id='UniversityMyEnrollmentComponent'>
      <MDBCardHeader className='enrollment-header'>
        <span className='header-title'>
          <i className='fas fa-graduation-cap mr-2'></i>
          My University Enrollment
        </span>
        {getStatusBadge()}
      </MDBCardHeader>
      <MDBCardBody>
        <div className='enrollment-details'>
          {/* Session Info */}
          {session && (
            <div className='session-info'>
              <div className='info-row'>
                <span className='info-label'>
                  <i className='fas fa-calendar-alt mr-2'></i>
                  Session Date
                </span>
                <span className='info-value'>{formatDateRange(session.start_date, session.end_date)}</span>
              </div>

              {session.location && (
                <div className='info-row'>
                  <span className='info-label'>
                    <i className='fas fa-map-marker-alt mr-2'></i>
                    Location
                  </span>
                  <span className='info-value'>
                    {session.location.name}
                    {session.location.address && (
                      <small className='d-block text-muted'>{session.location.address}</small>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Status Timeline */}
          <div className='status-timeline'>
            <div className={`timeline-item ${enrollment.requested_at || enrollment.invited_at ? 'completed' : ''}`}>
              <div className='timeline-icon'>
                <i className='fas fa-user-plus'></i>
              </div>
              <span>{enrollment.invited_at ? 'Invited' : 'Enrolled'}</span>
            </div>

            {enrollment.invited_at && (
              <div className={`timeline-item ${enrollment.accepted_at ? 'completed' : ''}`}>
                <div className='timeline-icon'>
                  <i className='fas fa-thumbs-up'></i>
                </div>
                <span>Accepted</span>
              </div>
            )}

            <div className={`timeline-item ${enrollment.paid_at || paymentSuccess ? 'completed' : ''}`}>
              <div className='timeline-icon'>
                <i className='fas fa-credit-card'></i>
              </div>
              <span>Paid</span>
            </div>

            <div className={`timeline-item ${enrollment.initiated_at ? 'completed' : ''}`}>
              <div className='timeline-icon'>
                <i className='fas fa-door-open'></i>
              </div>
              <span>Checked In</span>
            </div>

            <div className={`timeline-item ${enrollment.completed_at ? 'completed' : ''}`}>
              <div className='timeline-icon'>
                <i className='fas fa-award'></i>
              </div>
              <span>Completed</span>
            </div>
          </div>

          {/* Payment Verification State */}
          {isVerifyingPayment && (
            <div className='alert alert-info mt-3'>
              <MDBSpinner small className='mr-2' />
              Verifying your payment with Stripe...
            </div>
          )}

          {/* Payment Success Message */}
          {paymentSuccess && (
            <div className='alert alert-success mt-3'>
              <i className='fas fa-check-circle mr-2'></i>
              Payment successful! Your deposit has been received.
            </div>
          )}

          {/* Action Buttons */}
          <div className='enrollment-actions'>
            {needsToAccept && (
              <MDBBtn
                type='button'
                color='primary'
                onClick={handleAcceptInvitation}
                disabled={UniversityStore.isLoading.enrolling}
              >
                {UniversityStore.isLoading.enrolling ? (
                  <>
                    <MDBSpinner small className='mr-2' />
                    Accepting...
                  </>
                ) : (
                  <>
                    <i className='fas fa-check mr-2'></i>
                    Accept Invitation
                  </>
                )}
              </MDBBtn>
            )}

            {needsToPay && !isVerifyingPayment && !paymentSuccess && (
              <MDBBtn type='button' color='success' onClick={handlePayDeposit} disabled={isProcessingPayment}>
                {isProcessingPayment ? (
                  <>
                    <MDBSpinner small className='mr-2' />
                    Redirecting to payment...
                  </>
                ) : (
                  <>
                    <i className='fas fa-credit-card mr-2'></i>
                    Pay Deposit (${UniversityStore.depositAmount})
                  </>
                )}
              </MDBBtn>
            )}

            {(enrollment.paid_at || paymentSuccess) && !enrollment.initiated_at && (
              <div className='ready-message'>
                <i className='fas fa-check-circle text-success mr-2'></i>
                You're all set! See you at the training session.
              </div>
            )}
          </div>

          {/* Error Message */}
          {paymentError && (
            <div className='alert alert-danger mt-3'>
              <i className='fas fa-exclamation-circle mr-2'></i>
              {paymentError}
            </div>
          )}

          {/* Deposit Info */}
          {!enrollment.paid_at && !paymentSuccess && (
            <div className='deposit-info mt-3'>
              <small className='text-muted'>
                <i className='fas fa-info-circle mr-1'></i>
                By enrolling, you agree to attend the selected training session. A ${UniversityStore.depositAmount}{' '}
                refundable deposit is required to secure your spot and will be refunded upon attendance.
              </small>
            </div>
          )}
        </div>
      </MDBCardBody>
    </MDBCard>
  )
})

export default MyEnrollment
